import { useState } from 'react';
import moment from 'jalali-moment';
import styled from 'styled-components';
import { Box, Grid } from '@mui/material';
import { useSearchParams } from 'react-router-dom';

import { PatientSmbgData } from './smbg-data/smbg-container';
import { PieChartContainer } from './pie-chart/pie-chart-container';
import { HypoContainer } from './hypo-chart/hypo-container';
import {
  BarChartLoading,
  LastItemsLoading,
  PieChartLoading,
  RiskAnalysisLoading,
  SmbgLoading
} from './loading/report-loadings';
import { DatePickerModal } from './modal/date-picker-modal';
import { ReportDaySelection } from './day-selection/container';
import { AskAndAppreciate } from './ask-appreciate/ask-appreciate';
import { TherapeuticAdjusment } from './therapeutic-adjustment/therapeutic-content';
import { AlertText } from './alert-text/alert-text';

import {
  useCustomDateHypo,
  useCustomDateHypoList
} from 'modules/report/hooks/useHypo';
import { useGetPatientInfo } from 'modules/report/hooks/usePatientInfo';
import {
  useGet365DayReportData,
  useCustomDateReport,
  useReview
} from 'modules/report/hooks/useReport';
import { ReportSummary } from './report-summary/report-summary';

const is365DaysAgo = (from, to) => {
  const fromDate = moment(from, 'jYYYY-jMM-jDD');
  const toDate = moment(to, 'jYYYY-jMM-jDD');
  const dateRange = toDate.diff(fromDate, 'days');
  return dateRange <= 365 ? true : false;
};

export function PatientReview() {
  const [searchParams, setSearchParams] = useSearchParams();
  const patientId = searchParams.get('patientId');
  const [chosen, setChosen] = useState(14);
  const [items, setItems] = useState([7, 14, 30, 90, 365, 'Date Selection']);

  const [showDateModal, setShowDateModal] = useState(false);
  const [date, setDate] = useState({
    from: '',
    to: ''
  });
  const { data: patientInfo } = useGetPatientInfo(patientId);
  const { isPregnant } = patientInfo || {};
  const customDateReport = useCustomDateReport(date);
  const getCustomDateHypo = useCustomDateHypo(date);
  const getCustomDateHypoList = useCustomDateHypoList(date);

  const results = useReview(patientId, chosen);
  const isLoading = results.some((result) => result.isLoading);
  const { data: reportData } = results[0];
  const { data: hypoData } = results[1];

  const { data: report365DayData, fetchStatus: yearReportFetchStatus } =
    useGet365DayReportData(patientId, chosen);

  const getUserHypoList = () => {
    if (is365DaysAgo(date?.from, date?.to)) {
      getCustomDateHypoList.mutate({
        date,
        patientId
      });
    }
  };

  const getCustomDateReport = () => {
    getCustomDateHypo.mutate({
      date,
      patientId
    });
    customDateReport.mutate({
      date,
      patientId
    });

    closeDateModalHandler();
    const value = `From ${date.from} to ${date.to}`;
    setChosen(value);
    setItems((prev) => [...prev, value]);
  };

  const openDateModalHandler = () => setShowDateModal(true);
  const closeDateModalHandler = () => {
    setShowDateModal(false);
    if (items.slice(-1) == 'Date Selection') {
      setChosen(14);
    } else {
      setChosen(items.slice(-1));
    }
  };

  const loading =
    typeof chosen === 'number'
      ? isLoading || yearReportFetchStatus === 'fetching'
      : customDateReport.isLoading || getCustomDateHypo.isLoading;

  const showSelectedDateReport =
    typeof chosen !== 'number' && chosen !== 'Date Selection';

  const selectedRangeData = showSelectedDateReport
    ? customDateReport?.data
    : +chosen === 365
    ? report365DayData
    : { ...reportData?.[chosen] };

  const Loading = () => (
    <Grid item xs={12} mx={'16px'}>
      <SmbgLoading />
      <Grid
        container
        sx={{
          alignItems: 'flex-start',
          bgcolor: '#EEEEEE',
          borderRadius: '16px'
        }}
      >
        <PieChartLoading />
        <BarChartLoading />
      </Grid>
      <RiskAnalysisLoading />
      <LastItemsLoading />
    </Grid>
  );

  const estimateStatus =
    selectedRangeData?.estimateStatus == 2
      ? 'suspictionEstimate'
      : selectedRangeData?.estimateStatus == 3
      ? 'wrongEstimate'
      : null;

  return (
    <StyledReview>
      <ReportDaySelection
        items={items}
        chosen={chosen}
        setChosen={setChosen}
        openDateModalHandler={openDateModalHandler}
      />
      <Box
        sx={{
          padding: { xs: '30px 0', md: '64px' },
          display: 'grid',
          gap: { xs: '18px', md: '40px' }
        }}
      >
        <AlertText value={estimateStatus} />
        <AlertText value={isPregnant && 'isPregnant'} />
      </Box>
      {loading ? (
        <Loading />
      ) : (
        <div className="charts">
          <PatientSmbgData selectedRangeData={selectedRangeData} />
          <PieChartContainer
            selectedRangeData={selectedRangeData}
            patientId={patientId}
          />
          <HypoContainer
            disableGetListDataBtn={
              typeof chosen !== 'number' && !is365DaysAgo(date?.from, date?.to)
            }
            customDateListLoading={getCustomDateHypoList.isLoading}
            getListData={getUserHypoList}
            patientId={patientId}
            daysAgo={chosen}
            date={date}
            customDateData={getCustomDateHypo?.data}
            hypoData={hypoData}
          />
          <AskAndAppreciate />
          <TherapeuticAdjusment />
          {/* {selectedRangeData?.totalRecordCount > 0 && (
            <div
              className="review__summary-divider"
              style={{ width: '100%' }}
            />
          )}
          <ReportSummary
            summary={selectedRangeData.summary}
            show={selectedRangeData?.totalRecordCount > 0}
          /> */}
          <DatePickerModal
            date={date}
            setDate={setDate}
            patientId={patientId}
            getData={getCustomDateReport}
            isOpen={showDateModal}
            closeHandler={closeDateModalHandler}
          />
        </div>
      )}
    </StyledReview>
  );
}

const StyledReview = styled.div`
  width: 100%;
  padding: 0 16px;
  height: max-content;
  .charts {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    place-items: center;
    gap: 24px;
  }
  @media screen and (min-width: 1024px) {
    padding: 0;
    border-radius: 16px;
    .charts {
      gap: 0;
    }
    .review__summary-divider {
      width: 100%;
      height: 2px;
      background-color: #d9d9d9;
      margin-bottom: 35px;
    }
  }
`;
